import { FC, useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Textarea,
  Button,
  Avatar,
  Badge,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { Question, createQuestion } from '../../services/university-service';
import { useTranslation } from 'react-i18next';

interface QuestionsProps {
  questions: Question[];
  contentId: number;
  onQuestionAdded?: () => void;
}

const Questions: FC<QuestionsProps> = ({ questions, contentId, onQuestionAdded }) => {
  const [newQuestion, setNewQuestion] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();
  const { t } = useTranslation();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'white');

  const handleQuestionSubmit = async () => {
    if (!newQuestion.trim()) return;

    try {
      setIsSubmitting(true);
      await createQuestion({
        content: newQuestion.trim(),
        contentId,
        active: true,
        isAnswered: false,
      });

      toast({
        title: t('university.questions.success.sent.title'),
        description: t('university.questions.success.sent.description'),
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      setNewQuestion('');
      onQuestionAdded?.();
    } catch (error: any) {
      toast({
        title: t('university.questions.error.sending'),
        description: error.message || t('university.questions.error.try_again'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) return t('university.questions.time.now');
    if (diffInHours < 24) return t('university.questions.time.hours_ago', { hours: diffInHours, count: diffInHours });

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return t('university.questions.time.days_ago', { days: diffInDays, count: diffInDays });

    return date.toLocaleDateString();
  };

  const renderQuestion = (question: Question) => (
    <Box key={question.id} mb={6}>
      <HStack align="start" spacing={3} mb={3}>
        <Avatar
          size="sm"
          src={question.user?.name ? `https://ui-avatars.com/api/?name=${question.user.name}&background=random` : undefined}
          name={question.user?.name || t('university.questions.user.default')}
        />
        <Box flex={1}>
          <HStack justify="space-between" mb={1}>
            <Text fontWeight="medium" fontSize="sm" color={textColor}>
              {question.user?.name || t('university.questions.user.default')}
            </Text>
            <HStack spacing={2}>
              <Badge colorScheme={question.isAnswered ? 'green' : 'orange'} size="sm">
                {question.isAnswered ? t('university.questions.status.answered') : t('university.questions.status.waiting')}
              </Badge>
              <Text fontSize="xs" color="gray.500">
                {formatTimestamp(question.createdAt)}
              </Text>
            </HStack>
          </HStack>
          <Text fontSize="sm" color={textColor}>
            {question.content}
          </Text>
        </Box>
      </HStack>

      {question.adminReply && (
        <Box
          ml={8}
          p={3}
          bg={useColorModeValue('blue.50', 'blue.900')}
          borderRadius="md"
          borderLeft="3px solid"
          borderColor="blue.500"
        >
          <HStack justify="space-between" mb={1}>
            <HStack>
              <Avatar
                size="xs"
                name={question.adminUser?.name || t('university.questions.instructor.default')}
                bg="blue.500"
              />
              <Text fontWeight="medium" fontSize="sm" color="blue.600">
                {question.adminUser?.name || t('university.questions.instructor.default')}
              </Text>
            </HStack>
            <Text fontSize="xs" color="gray.500">
              {question.adminReplyAt && formatTimestamp(question.adminReplyAt)}
            </Text>
          </HStack>
          <Text fontSize="sm" color={textColor}>
            {question.adminReply}
          </Text>
        </Box>
      )}
    </Box>
  );

  return (
    <Box p={6} bg={bgColor} borderRadius="lg" border="1px" borderColor={borderColor}>
      <Text fontSize="lg" fontWeight="semibold" mb={4} color={textColor}>
        {t('university.questions.title', { count: questions.length })}
      </Text>
      <Text fontSize="sm" color="gray.500" mb={6}>
        {t('university.questions.description')}
      </Text>

      {/* New question form */}
      <VStack spacing={3} align="stretch" mb={6}>
        <Textarea
          placeholder={t('university.questions.placeholder')}
          value={newQuestion}
          onChange={(e) => setNewQuestion(e.target.value)}
          rows={3}
          bg={useColorModeValue('gray.50', 'gray.700')}
          resize="vertical"
        />
        <HStack justify="flex-end">
          <Button
            size="sm"
            colorScheme="blue"
            onClick={handleQuestionSubmit}
            isDisabled={!newQuestion.trim() || isSubmitting}
            isLoading={isSubmitting}
            loadingText={t('university.questions.sending')}
          >
            {t('university.questions.send_question')}
          </Button>
        </HStack>
      </VStack>

      {/* Questions list */}
      {questions.length > 0 ? (
        <VStack align="stretch" spacing={6}>
          {questions.map(question => renderQuestion(question))}
        </VStack>
      ) : (
        <Text color="gray.500" textAlign="center" py={8}>
          {t('university.questions.no_questions')}
        </Text>
      )}
    </Box>
  );
};

export default Questions; 